/**
 * Sound utility for playing 8-bit style sound effects and background music
 * Using Howler.js for cross-platform compatibility, especially for iOS
 */
import { Howl, Howler } from 'howler';

type SoundCategory = 'ui' | 'combat' | 'music';
type UISoundType = 'select' | 'bubble_pop' | 'message_alert' | 'beep' | 'pluck';
type CombatSoundType = 'sword';

// Sound volume control (0.0 to 1.0)
const SOUND_VOLUME = 1.0; // Maximum volume for testing
const MUSIC_VOLUME = 0.1; // Default music volume

// Track the music state
let musicState = {
  active: false,  // Music is inactive by default
  currentTrack: '',
  howl: null as Howl | null
};

/**
 * Play a UI sound effect
 * @param sound The UI sound type to play
 */
export const playUISound = (sound: UISoundType): void => {
  playSoundEffect('ui', sound);
};

/**
 * Play a combat sound effect
 * @param sound The combat sound type to play
 */
export const playCombatSound = (sound: CombatSoundType): void => {
  playSoundEffect('combat', sound);
};

/**
 * Generic function to play any sound effect
 * @param category The sound category
 * @param sound The sound file name (without extension)
 * @param extension File extension (default is .wav)
 */
export const playSoundEffect = (category: SoundCategory, sound: string, extension: string = 'mp3'): void => {
  try {
    // Log to debug if sounds are being triggered
    console.log(`Playing sound: ${category}/${sound}.${extension}`);
    
    // Create Howl instance for the sound effect
    const soundEffect = new Howl({
      src: [`${process.env.PUBLIC_URL}/sounds/${category}/${sound}.${extension}`],
      volume: SOUND_VOLUME,
      html5: true, // This helps with mobile devices
      onplay: () => console.log('Audio started playing'),
      onloaderror: (id, err) => console.error('Audio loading error:', err),
      onplayerror: (id, err) => {
        console.error(`Error playing sound: ${category}/${sound}`, err);
        // Auto-unlock audio on mobile devices
        Howler.autoUnlock = true;
      }
    });
    
    // Play the sound
    soundEffect.play();
    
  } catch (error) {
    console.error(`Error creating audio for ${category}/${sound}:`, error);
  }
};

/**
 * Play background music
 * @param track The music track to play (without extension)
 * @param autoplay Whether to immediately start playing (default: false)
 */
export const playBackgroundMusic = (track: string = 'chiptune', autoplay: boolean = false): void => {
  try {
    // Check if we already have this track loaded and playing
    if (musicState.active && musicState.currentTrack === track && musicState.howl) {
      // If it's already the right track, just make sure it's playing
      if (autoplay && !musicState.howl.playing()) {
        musicState.howl.play();
      }
      return;
    }
    
    // Stop any currently playing music first
    stopBackgroundMusic();
    
    console.log(`Playing background music: ${track}.mp3`);
    
    // Create Howl instance for background music
    musicState.howl = new Howl({
      src: [`${process.env.PUBLIC_URL}/sounds/music/${track}.mp3`],
      volume: MUSIC_VOLUME,
      loop: true, // Enable looping
      html5: true, // Better for streaming music
      autoplay: autoplay, // Control whether it auto-plays
      onplay: () => {
        console.log('Background music started playing');
        musicState.active = true;
      },
      onpause: () => {
        console.log('Background music paused');
        musicState.active = false;
      },
      onstop: () => {
        console.log('Background music stopped');
        musicState.active = false;
      },
      onloaderror: (id, err) => console.error('Background music loading error:', err),
      onplayerror: (id, err) => {
        console.error(`Error playing background music: ${track}`, err);
        
        // For iOS, add a user interaction handler to start playback
        if (
          /iPhone|iPad|iPod|iOS/i.test(navigator.userAgent) || 
          /Safari/i.test(navigator.userAgent)
        ) {
          console.log('iOS/Safari detected. Adding document click handler for audio');
          // Don't automatically play music on interaction - keep it disabled by default
          // const playOnInteraction = () => {
          //   if (musicState.howl) {
          //     musicState.howl.play();
          //     document.removeEventListener('click', playOnInteraction);
          //     document.removeEventListener('touchend', playOnInteraction);
          //   }
          // };
          // 
          // document.addEventListener('click', playOnInteraction, { once: true });
          // document.addEventListener('touchend', playOnInteraction, { once: true });
        }
      }
    });
    
    // Update track info
    musicState.currentTrack = track;
    
    // Play the music if autoplay is true
    if (autoplay && musicState.howl) {
      musicState.howl.play();
    }
    
  } catch (error) {
    console.error(`Error creating audio for background music ${track}:`, error);
  }
};

/**
 * Pause the currently playing background music
 */
export const pauseBackgroundMusic = (): void => {
  if (musicState.howl) {
    musicState.howl.pause();
  }
};

/**
 * Resume the currently paused background music
 */
export const resumeBackgroundMusic = (): void => {
  if (musicState.howl) {
    // Only play if it's not already playing
    if (!musicState.howl.playing()) {
      musicState.howl.play();
      console.log('Background music resumed');
    }
  } else {
    // If we don't have music loaded, load the default
    playBackgroundMusic();
  }
};

/**
 * Stop the currently playing background music
 */
export const stopBackgroundMusic = (): void => {
  if (musicState.howl) {
    musicState.howl.stop();
    // Unload to free memory
    musicState.howl.unload();
    musicState.howl = null;
    musicState.active = false;
  }
};

/**
 * Set the volume of the background music
 * @param volume Volume level from 0.0 to 1.0
 */
export const setMusicVolume = (volume: number): void => {
  const clampedVolume = Math.max(0, Math.min(1, volume));
  if (musicState.howl) {
    musicState.howl.volume(clampedVolume);
    console.log(`Background music volume set to ${clampedVolume}`);
  }
};

/**
 * Test sound function - plays a sound that should definitely work
 * Call this from the console to troubleshoot audio issues
 */
export const testSound = (): void => {
  try {
    console.log('Testing sound system with beep...');
    
    // Create test Howl instance
    const testSound = new Howl({
      src: [`${process.env.PUBLIC_URL}/sounds/ui/beep.mp3`],
      volume: 1.0,
      onplay: () => console.log('Test sound started playing'),
      onend: () => console.log('Test sound played successfully'),
      onloaderror: (id, err) => console.error('Test sound loading error:', err),
      onplayerror: (id, err) => console.error('Test sound failed:', err)
    });
    
    // Play the test sound
    testSound.play();
    
  } catch (e) {
    console.error('Test sound creation failed:', e);
  }
};

// Convenience functions for specific sounds
export const playSelectSound = (): void => playUISound('pluck');
export const playBubbleSound = (): void => playUISound('pluck');
export const playMessageSound = (): void => playUISound('pluck');
export const playBeepSound = (): void => playSoundEffect('ui', 'beep', 'mp3');
export const playSwordSound = (): void => playCombatSound('sword');
export const playSwordHitSound = (): void => playCombatSound('sword');

/**
 * Global sound settings for Howler
 */
Howler.autoUnlock = true; // Helps with iOS/mobile
Howler.html5PoolSize = 10; // Increase for more concurrent sounds

// Initialize Howler with the right settings for iOS
const initHowler = () => {
  // For iOS, we need user interaction to start audio
  if (/iPhone|iPad|iPod|iOS/i.test(navigator.userAgent) || /Safari/i.test(navigator.userAgent)) {
    console.log('iOS/Safari detected. Setting up audio unlock');
    
    const unlockAudio = () => {
      // Create and play a silent sound to unlock audio
      const silentSound = new Howl({
        src: ['data:audio/mp3;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQsRbAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQMSkAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV'],
        volume: 0,
        onend: () => {
          console.log('Silent sound played - audio should be unlocked');
          document.removeEventListener('click', unlockAudio);
          document.removeEventListener('touchend', unlockAudio);
          
          // Don't auto-resume music - we want it disabled by default
          // Only commented out for reference:
          // if (musicState.howl && !musicState.howl.playing() && musicState.currentTrack) {
          //   musicState.howl.play();
          // }
        }
      });
      silentSound.play();
    };
    
    document.addEventListener('click', unlockAudio, { once: true });
    document.addEventListener('touchend', unlockAudio, { once: true });
  }
};

// Run initialization
initHowler();

// Run a test sound on module load (for debugging)
console.log('Sound utility loaded - run window.testSound() to verify audio');
(window as any).testSound = testSound;
(window as any).playBackgroundMusic = playBackgroundMusic;
(window as any).pauseBackgroundMusic = pauseBackgroundMusic;
(window as any).resumeBackgroundMusic = resumeBackgroundMusic;
(window as any).stopBackgroundMusic = stopBackgroundMusic;
(window as any).setMusicVolume = setMusicVolume;
